/**
 * OSMDIterator
 *
 * An iterator that works with OSMD note positions instead of pre-rendered JSON data.
 * This is used by CursorMobile and EventStream to track playback position
 * and process MIDI events when using the OSMD Phaser backend.
 */

import Phrase, { Iterator } from 'Models/Phrase';
import TimeSignature from 'Models/TimeSignature';
import { PrerenderedGraphics, PrerenderedPhraseData, PrerenderedVoiceEntry } from 'Types';
import IPlayable from 'Phaser/GameObjects/Playable';
import { ICursorIterator } from 'Phaser/GameObjects/CursorMobile';

/**
 * Represents a note's position data extracted from OSMD's GraphicSheet.
 */
export interface OSMDNoteData {
  /** X position in pixels */
  x: number;
  /** Y position in pixels */
  y: number;
  /** Width of the note bounding box */
  width: number;
  /** Height of the note bounding box */
  height: number;
  /** MIDI pitch number (60 = middle C) */
  midiPitch: number;
  /** Note name (e.g., "C4", "D#5") */
  noteName: string;
  /** Duration in beats (fraction of whole note) */
  duration: number;
  /** Measure number (1-based) */
  measureNumber: number;
  /** Voice number within the staff */
  voiceNumber: number;
  /** Staff number (0-based) */
  staffIndex: number;
  /** Whether this is a rest */
  isRest: boolean;
  /** The timestamp of this note in measure units (relative to phrase start) */
  timestamp: number;
  /** VexFlow element ID for this note */
  vexflowId: string;
  /** Tie: is this the first note of a tie group? undefined when the note is not tied. */
  isTieStart?: boolean;
  /** Tie: combined real-value duration of the whole tie group (same for start + continuations). */
  tieDuration?: number;
  /**
   * Tie: stable id shared by every head of the same OSMD NoteTie group, assigned
   * at the scene walk from the Tie object's identity. undefined when the note is
   * not tied. Lets convertOSMDToPrerenderedData bind each continuation head's
   * graphic + timestamp to the correct tie-start (identity, not pitch - a pitch
   * key can collide across voices/hands or an immediately re-tied same pitch).
   */
  tieGroupId?: string;
}

/**
 * Minimal structural view of an OSMD source note for tie extraction. Kept loose
 * so the helper is unit-testable with a mock note (the real OSMD `Note` is
 * structurally assignable).
 */
export interface TieReadableSourceNote {
  NoteTie?: { Notes: readonly unknown[]; Duration?: { RealValue: number } } | null;
  Length?: { RealValue: number } | null;
}

/**
 * Extract tie grouping from a live OSMD source note. Mirrors the live-OSMD
 * calculateTiedNoteLength + the prerenderer: the first note of a NoteTie group
 * (Notes[0] === sourceNote) is the tie-start and carries the whole tie as one
 * held note; Duration.RealValue is the combined hold (identical read from the
 * start or any continuation). Returns undefined fields for an untied note.
 *
 * Extracted from the OSMD scene notePositions loops so the read has unit
 * coverage (the scenes themselves are Phaser/OSMD-coupled and hard to test).
 */
export function extractTieFields(sourceNote: TieReadableSourceNote): {
  isTieStart?: boolean;
  tieDuration?: number;
} {
  const tie = sourceNote.NoteTie;
  if (!tie) {
    return { isTieStart: undefined, tieDuration: undefined };
  }
  return {
    isTieStart: tie.Notes[0] === sourceNote,
    tieDuration: tie.Duration?.RealValue ?? sourceNote.Length?.RealValue ?? 0,
  };
}

/**
 * Rank of a note's notehead within a same-hand chord, ordered by ascending
 * pitch (0 = lowest pitch). A same-hand chord is rendered by VexFlow as ONE
 * StaveNote carrying multiple noteheads that all share a single group id, so
 * the grading colour path (which addresses the shared NoteContainer by that id)
 * needs this rank to colour the individual head belonging to this note instead
 * of the whole StaveNote. `chordMidiNotes` is the set of MIDI pitches of every
 * note sharing this note's StaveNote id (i.e. the chord). For a single note the
 * chord is a singleton and the rank is 0. Duplicate pitches (not musically
 * expected within one hand) resolve to the first slot.
 *
 * Extracted as a pure function so the ranking has unit coverage independent of
 * the Phaser/OSMD render path (mirrors extractTieFields above).
 */
export function chordHeadRankAscending(
  midiNote: number,
  chordMidiNotes: readonly number[],
): number {
  const sorted = [...chordMidiNotes].sort((a, b) => a - b);
  const rank = sorted.indexOf(midiNote);
  return rank < 0 ? 0 : rank;
}

/**
 * Interface for getting note positions from an OSMD-rendered phrase.
 * Extends the base INoteGraphicsDatabase interface for compatibility with CursorMobile.
 */
export interface IOSMDNotePositionProvider {
  /** Get the X position of a note by its VexFlow ID */
  getNoteXPosition(id: string, timestamp?: number, isRest?: boolean): number | undefined;
  /** Get the end position of the phrase - alias for getPhraseEndPosition */
  getPhraseEndPosition(): number;
  /** Get note position (for INoteGraphicsDatabase compatibility) */
  getNotePosition(id: string, timestamp?: number, rest?: boolean): number | null;
  /** Get note by ID (for INoteGraphicsDatabase compatibility) */
  getNoteById(id: string, phraseTimestamp?: number): IPlayable | null;
}

/**
 * Converts OSMD note data to PrerenderedPhraseData format.
 * This allows the existing Iterator and EventStream classes to work with OSMD data.
 */
export function convertOSMDToPrerenderedData(
  noteData: OSMDNoteData[],
  startTimestamp: number,
): PrerenderedPhraseData[] {
  // Group notes by timestamp
  const notesByTimestamp = new Map<number, OSMDNoteData[]>();
  for (const note of noteData) {
    const relativeTimestamp = note.timestamp - startTimestamp;
    const key = Math.round(relativeTimestamp * 10000) / 10000; // Round to avoid floating point issues
    if (!notesByTimestamp.has(key)) {
      notesByTimestamp.set(key, []);
    }
    notesByTimestamp.get(key)!.push(note);
  }

  // Notehead highlighting: pre-compute, per tie group, the ordered set of its
  // heads (start first, then continuations by timestamp), each carrying its own
  // stave-note graphic and its phrase-relative timestamp. Only the tie-START's
  // payload receives this list (below); continuations still emit their own voice
  // entries so addEvents2's fold can skip them, but their graphics are captured
  // here so the whole tie can highlight off the single held event. A group with
  // a single head is left out - nothing to merge, so it behaves as an untied note.
  const headsByGroup = new Map<string, { graphic: PrerenderedGraphics; timestamp: number }[]>();
  for (const note of noteData) {
    if (note.isRest || note.tieGroupId === undefined) continue;
    const relativeTimestamp = Math.round((note.timestamp - startTimestamp) * 10000) / 10000;
    const entry = {
      graphic: { staveNote: { id: note.vexflowId, left: note.x } },
      timestamp: relativeTimestamp,
    };
    const existing = headsByGroup.get(note.tieGroupId);
    if (existing) existing.push(entry);
    else headsByGroup.set(note.tieGroupId, [entry]);
  }
  for (const heads of headsByGroup.values()) {
    heads.sort((a, b) => a.timestamp - b.timestamp);
  }

  // Convert to PrerenderedPhraseData format
  const result: PrerenderedPhraseData[] = [];
  const sortedTimestamps = Array.from(notesByTimestamp.keys()).sort((a, b) => a - b);

  for (const timestamp of sortedTimestamps) {
    const notes = notesByTimestamp.get(timestamp)!;
    const measureIndex = Math.max(notes[0].measureNumber - 1, 0);

    const voiceEntries: PrerenderedVoiceEntry[] = notes.map((note) => ({
      Notes: [
        {
          fingering: undefined,
          graphics: [{ staveNote: { id: note.vexflowId, left: note.x } }],
          isRest: note.isRest,
          pitch: {
            accidental: 0,
            fundamentalNote: 0,
            octave: 0,
            halfTone: note.midiPitch - 12, // Convert MIDI to halfTone
            frequency: 440 * Math.pow(2, (note.midiPitch - 69) / 12), // A4 = 440Hz
          },
          // Emit tie grouping when the extractor captured it (live-OSMD path).
          // undefined isTieStart => the note is not tied. The tie-START also
          // carries the ordered head set (graphic + timestamp) for notehead
          // highlighting, but only when the group actually has >1 head.
          noteTie:
            note.isTieStart === undefined
              ? undefined
              : {
                  isTieStart: note.isTieStart,
                  tieDuration: note.tieDuration ?? note.duration,
                  ...(note.isTieStart &&
                  note.tieGroupId !== undefined &&
                  (headsByGroup.get(note.tieGroupId)?.length ?? 0) > 1
                    ? { heads: headsByGroup.get(note.tieGroupId) }
                    : {}),
                },
          length: note.duration,
          printObject: true,
          staffPosition: note.staffIndex === 0 ? 'top' : 'bottom',
        },
      ],
    }));

    result.push({
      currentTimestamp: timestamp,
      currentMeasureIndex: measureIndex,
      currentVoiceEntries: voiceEntries,
    });
  }

  return result;
}

/**
 * OSMD-specific iterator that extends the base Iterator class.
 * Works with OSMD note positions instead of pre-rendered JSON data.
 * Implements ICursorIterator for compatibility with CursorMobile.
 */
export class OSMDIteratorExtensible extends Iterator implements ICursorIterator {
  totalLength: number;
  positionProvider: IOSMDNotePositionProvider;
  starts: number[] = [0];
  lastPlayedMeasure: number = 0;
  timestampOffset: number = 0;
  measureTimestamps: number[] = [];
  shouldIncrementMeasureIndex: boolean = false;

  constructor(
    positionProvider: IOSMDNotePositionProvider,
    osmdNoteData: OSMDNoteData[],
    phrase: Phrase,
    timeSig: TimeSignature,
    timestampOffset?: number,
  ) {
    // Convert OSMD data to PrerenderedPhraseData format
    const cursorData = convertOSMDToPrerenderedData(osmdNoteData, phrase.getStartTimestamp());
    super(cursorData, phrase);

    this.positionProvider = positionProvider;
    this.totalLength = this.calculateTotalLength(cursorData);
    this.timestampOffset = timestampOffset ?? 0;

    // Calculate measure timestamps
    const measureDuration = (1 / timeSig.denominator) * timeSig.numerator;
    const numMeasures =
      cursorData.length > 0 ? Math.max(...cursorData.map((d) => d.currentMeasureIndex)) + 1 : 0;
    this.measureTimestamps = Array.from({ length: numMeasures }, (_, idx) => measureDuration * idx);
  }

  private calculateTotalLength(cursorData: PrerenderedPhraseData[]): number {
    if (cursorData.length === 0) return 0;

    let maxTimestamp = 0;
    let maxDuration = 0;

    for (const slice of cursorData) {
      if (slice.currentTimestamp > maxTimestamp) {
        maxTimestamp = slice.currentTimestamp;
        maxDuration = Math.max(
          ...slice.currentVoiceEntries.map(({ Notes }) =>
            Math.max(...Notes.map(({ length }) => length)),
          ),
        );
      }
    }

    return maxTimestamp + maxDuration;
  }

  /**
   * Add another phrase's data to this iterator.
   * Accepts PrerenderedPhraseData[] for compatibility with ICursorIterator interface.
   */
  addPhrase(
    cursorData: PrerenderedPhraseData[],
    timeSig: TimeSignature,
    _phraseId: string,
    _phraseIndex?: number,
  ): void {
    const newCursorData = cursorData;

    // Offset timestamps by current total length
    const offsetData = newCursorData
      .map((item) => ({
        ...item,
        currentTimestamp: item.currentTimestamp + this.totalLength,
        currentVoiceEntries: item.currentVoiceEntries.filter(
          (entry) => entry.Notes.filter((note) => note.printObject).length > 0,
        ),
      }))
      .filter(({ currentVoiceEntries }) => currentVoiceEntries.length > 0);

    this.cursorData.push(...offsetData);

    // Update measure timestamps
    const lastMeasure = this.measureTimestamps.length;
    const measureDuration = (1 / timeSig.denominator) * timeSig.numerator;
    const newMeasureCount =
      newCursorData.length > 0
        ? Math.max(...newCursorData.map((d) => d.currentMeasureIndex)) + 1
        : 0;

    this.measureTimestamps.push(
      ...Array.from({ length: newMeasureCount }, (_, idx) => measureDuration * (idx + lastMeasure)),
    );

    this.totalLength += this.calculateTotalLength(newCursorData);
    this.endReached = false;
  }

  lastNoteDuration(): number {
    if (this.cursorData.length === 0) return 0;
    const lastSlice = this.cursorData[this.cursorData.length - 1];
    return this.totalLength - lastSlice.currentTimestamp;
  }

  getPhraseEndPosition(): number | undefined {
    return this.positionProvider.getPhraseEndPosition();
  }

  getGraphicsLeft(currentNoteId: string, rest?: boolean): number | undefined {
    const pos = this.positionProvider.getNoteXPosition(
      currentNoteId,
      this.currentTimeStamp + this.timestampOffset,
      rest,
    );
    return pos ?? undefined;
  }

  totalDuration(): number {
    return this.totalLength;
  }

  getLastPlayedMeasure(): number {
    return this.lastPlayedMeasure;
  }

  left(_dbg?: boolean): number {
    try {
      if (this.EndReached) {
        return this.getPhraseEndPosition()!;
      } else {
        const notes = this.CurrentVoiceEntries.flatMap((entry) =>
          entry.Notes.filter((note) => !note.isRest).map((note) =>
            this.getGraphicsLeft(note.graphics[0].staveNote.id),
          ),
        ).filter((pos): pos is number => pos !== undefined);
        const positions =
          notes.length !== 0
            ? notes
            : this.CurrentVoiceEntries.flatMap((entry) =>
                entry.Notes.filter((note) => note.printObject).map((note) => {
                  const pos = this.getGraphicsLeft(note.graphics[0].staveNote.id, note.isRest);
                  return pos;
                }),
              ).filter((pos): pos is number => pos !== undefined);
        if (positions.length === 0) {
          // No valid positions found — use the phrase end position as a
          // safe fallback rather than 0 (left screen edge), which would
          // cause the cursor to jump to the start of the staff system.
          console.warn(
            '[OSMDIterator.left] No valid note positions found, using phrase end as fallback',
          );
          return this.getPhraseEndPosition() ?? 0;
        }
        return Math.min(...positions);
      }
    } catch (error) {
      console.error('Error when getting the x-position of the next note to play.', error);
      // Return the phrase end position as fallback instead of 0 (left
      // screen edge). Returning 0 causes the cursor to jump to the very
      // start of the staff system on errors, which is the exact symptom
      // of the phrase-transition cursor positioning bug.
      return this.getPhraseEndPosition() ?? 0;
    }
  }

  getNextTimestamp(): number {
    if (this.EndReached) {
      return this.totalLength;
    } else {
      return this.currentTimeStamp;
    }
  }

  next(
    tempo?: number,
    onLastSlice?: (start: number, duration: number, durationInSecs: number) => void,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Iterator callback context from parent
    ctx?: any,
  ): void {
    super.next();

    if (this.shouldIncrementMeasureIndex) {
      this.lastPlayedMeasure++;
      this.shouldIncrementMeasureIndex = false;
    } else if (this.currentMeasureIndex > this.lastPlayedMeasure) {
      this.starts[this.currentMeasureIndex] = this.indx;
      this.shouldIncrementMeasureIndex = true;
    }

    if (tempo) {
      const durationInSecs = (this.totalDuration() - this.currentTimeStamp) * (60 / tempo) * 4;
      if (this.endReached) {
        onLastSlice?.bind(ctx)(
          this.currentTimeStamp,
          this.totalDuration() - this.currentTimeStamp,
          durationInSecs,
        );
      }
    }
  }

  /**
   * Ensure the starts array is populated up to the target measure by
   * advancing the iterator from the beginning. This is needed because
   * starts[] is only populated as the cursor plays through measures.
   * After populating, the iterator is left at the target measure position.
   */
  ensureStartsPopulated(targetMeasure: number): void {
    if (targetMeasure < this.starts.length && this.starts[targetMeasure] !== undefined) {
      return; // Already populated
    }

    // Reset to beginning and advance to populate starts
    this.resetIterator();
    this.starts = [0];
    this.lastPlayedMeasure = 0;
    this.shouldIncrementMeasureIndex = false;

    while (!this.endReached && this.lastPlayedMeasure <= targetMeasure) {
      this.next(0);
    }
  }

  currentPhraseEndTimestamp(): number {
    return this.totalLength;
  }

  checkPhraseCompletion(
    timestamp: number,
    _eligible: boolean,
    onFinishPlaying?: (eligible?: boolean) => void,
    cbCtx?: object,
  ): void {
    if (this.indx === 0) return;
    if (timestamp >= this.totalLength) {
      onFinishPlaying?.call(cbCtx, true);
    }
  }

  /**
   * Rewind the iterator to the start of `measure`. If `timestamp` is provided
   * AND falls within this phrase's local duration, also advance slice-by-slice
   * until `currentTimeStamp` reaches it. This lets callers place the iterator
   * at any timestamp within a measure (e.g. a loop bracket starting on beat 4),
   * not just measure boundaries.
   *
   * Without the timestamp advance, mid-measure positioning leaves the iterator
   * behind the cursor's interpolation reference, and the cursor's update() loop
   * fires `shouldAdvance` repeatedly to catch up — overwriting interpolation
   * state with stale slice positions and flashing the cursor visibly.
   *
   * Returns the bar it rolled back to.
   */
  rollbackToMeasure(measure: number, timestamp?: number): number {
    this.endReached = false;
    const target = this.measureStart(measure);
    this.indx = target.startIndex - 1;
    // Reset lastPlayedMeasure so subsequent getLastPlayedMeasure() reflects
    // the new position, not a stale value from before the rollback.
    this.lastPlayedMeasure = Math.max(target.measure - 1, 0);
    this.shouldIncrementMeasureIndex = false;
    this.next(0);

    if (timestamp === undefined) return target.measure;

    // Defensive: skip the advance if the target timestamp lies outside this
    // phrase's local duration. A few legacy callers pass a phrase's GLOBAL
    // start timestamp (instead of a local one) when resetting the cursor of
    // a phrase being deactivated; advancing the iterator past its end would
    // leave a stale `endReached` state behind that pollutes future operations.
    const phraseLocalDuration = this.totalDuration();
    if (timestamp > phraseLocalDuration + 1e-6) return target.measure;

    let steps = 0;
    const SAFETY_LIMIT = 1024;
    while (this.currentTimeStamp + 1e-6 < timestamp && !this.endReached && steps < SAFETY_LIMIT) {
      this.next();
      steps++;
    }
    return target.measure;
  }

  /**
   * The first slice of a bar, found by the bar's number in the slice data.
   * `starts` cannot be used for this: it is filled as the iterator plays, and
   * it skips a bar of one slice that follows another (whole notes), which left
   * such a bar with no start (NaN). A bar with no slice of its own lands on
   * the nearest bar before it, and a bar before the first on the first.
   */
  private measureStart(measure: number): { startIndex: number; measure: number } {
    const barStarts = this.cursorData
      .map((slice, startIndex) => ({ startIndex, measure: slice.currentMeasureIndex }))
      .filter((start, index, all) => index === 0 || start.measure !== all[index - 1].measure);
    const exact = barStarts.find((start) => start.measure === measure);
    if (exact) return exact;
    const before = barStarts.filter((start) => start.measure < measure);
    if (before.length > 0) return before[before.length - 1];
    return barStarts[0] ?? { startIndex: 0, measure: 0 };
  }
}
